import {
  EnvelopeIcon,
  EnvelopeOpenIcon,
  FireIcon,
  PaperAirplaneIcon,
  PhoneArrowDownLeftIcon,
  PhoneXMarkIcon,
  StarIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/solid";
import {
  CalendarCheckIcon,
  CalendarSync,
  Check,
  Clock8,
  MessageCircleOff,
  X,
} from "lucide-react";

import { AccountUserListTypeI } from "shared/lib/interfaces/account";
import { LIST_STATUS_MEETING_FEEDBACK } from "shared/lib/constants/dispositions";
import { HubspotIcon } from "shared/ui/icons/brands/hubspot";
import { SalesforceIcon } from "shared/ui/icons/brands/salesforce";

/**
 * System lists are the lists that are created by the system and are not editable
 * by a user
 */
export const SYSTEM_LIST_TYPES = {
  CALLBACKS: "callbacks",
  MISSED_CALLS: "missed_calls",
  BOOKED_MEETINGS: "booked_meetings",
  EMAIL_ACTIONS: "email_actions",
  FEEDBACK_RECEIVED: "feedback_received",
  NO_SOWS_AND_CANCELS: "no_show_and_cancels",
  // This list will be displayed as "Hot Email Responses" in the UI
  COLD_OUTREACH_RESPONSE: "cold_outreach_response",
  HUBSPOT_LEAD_PULLS: "hubspot_lead_pulls",
  SALESFORCE_LEAD_PULLS: "salesforce_lead_pulls",
  // C-16 S4: up to 10 nurtures the caller never wants to lose. Filled only
  // by "Add to Top 10", never by adding leads to a list.
  TOP_TEN: "top_ten",
} as const;

export const SYSTEM_LIST_TITLES = {
  [SYSTEM_LIST_TYPES.BOOKED_MEETINGS]: "Booked Meetings",
  [SYSTEM_LIST_TYPES.CALLBACKS]: "Callbacks",
  [SYSTEM_LIST_TYPES.EMAIL_ACTIONS]: "Email Actions",
  [SYSTEM_LIST_TYPES.FEEDBACK_RECEIVED]: "Feedback Received",
  [SYSTEM_LIST_TYPES.MISSED_CALLS]: "Missed Calls",
  [SYSTEM_LIST_TYPES.COLD_OUTREACH_RESPONSE]: "Hot Email Responses",
  [SYSTEM_LIST_TYPES.HUBSPOT_LEAD_PULLS]: "HubSpot Synced Leads",
  [SYSTEM_LIST_TYPES.SALESFORCE_LEAD_PULLS]: "Salesforce Synced Leads",
  [SYSTEM_LIST_TYPES.TOP_TEN]: "Top 10",
};

/**
 * This map covers all supported list types, including custom (created by user)
 * exclusive lists (they are emitted for top performing callers) and system lists
 */
export const LIST_TYPES = {
  CUSTOM: "custom",
  EXCLUSIVE: "top_caller_dedicated",
  TODO_LIST: "todo_list",
  ...SYSTEM_LIST_TYPES,
} as const;

/**
 * User is disallowed to "remove from" these lists
 */
export const LISTS_WITH_PROTECTED_LEADS: AccountUserListTypeI[] = [
  SYSTEM_LIST_TYPES.BOOKED_MEETINGS,
];

export const LISTS_ICON_MAP = {
  [SYSTEM_LIST_TYPES.BOOKED_MEETINGS]: <CalendarCheckIcon className="w-4" />,
  [SYSTEM_LIST_TYPES.CALLBACKS]: <PhoneArrowDownLeftIcon className="w-4" />,
  [SYSTEM_LIST_TYPES.EMAIL_ACTIONS]: <EnvelopeOpenIcon className="w-4" />,
  [SYSTEM_LIST_TYPES.FEEDBACK_RECEIVED]: <StarIcon className="w-4" />,
  [SYSTEM_LIST_TYPES.MISSED_CALLS]: <PhoneXMarkIcon className="w-4" />,
  [SYSTEM_LIST_TYPES.NO_SOWS_AND_CANCELS]: <XCircleIcon className="w-4" />,
  [SYSTEM_LIST_TYPES.COLD_OUTREACH_RESPONSE]: (
    <PaperAirplaneIcon className="w-4 -rotate-45" />
  ),
  [SYSTEM_LIST_TYPES.HUBSPOT_LEAD_PULLS]: <HubspotIcon className="w-4" />,
  [SYSTEM_LIST_TYPES.SALESFORCE_LEAD_PULLS]: <SalesforceIcon className="w-4" />,
  [SYSTEM_LIST_TYPES.TOP_TEN]: <FireIcon className="w-4" />,
};

export const LIST_STATUS_PILL_VARIANTS = {
  [SYSTEM_LIST_TYPES.CALLBACKS]: {
    icon: PhoneArrowDownLeftIcon,
    color: "text-white",
    bg: "bg-[#F8A467]",
  },
  [SYSTEM_LIST_TYPES.MISSED_CALLS]: {
    icon: XMarkIcon,
    color: "text-[#B90000]",
    bg: "bg-[#FEDFDF]",
  },
  [SYSTEM_LIST_TYPES.BOOKED_MEETINGS]: {
    icon: CalendarCheckIcon,
    color: "text-white",
    bg: "bg-[#F86767]",
  },
  [SYSTEM_LIST_TYPES.EMAIL_ACTIONS]: {
    icon: EnvelopeIcon,
    color: "text-[#3B6E29]",
    bg: "bg-[#EDFFDF]",
  },
  [SYSTEM_LIST_TYPES.COLD_OUTREACH_RESPONSE]: {
    icon: EnvelopeIcon,
    color: "text-white",
    bg: "app-gradient py-1.5 w-[110px] flex-center",
  },
  [SYSTEM_LIST_TYPES.HUBSPOT_LEAD_PULLS]: {
    icon: HubspotIcon,
    color: "text-black",
    bg: "bg-[#eee]",
  },
} as const;

// Dark-green checkmark and text on light-green background
const POSITIVE_OUTCOME_CONFIG = {
  icon: Check,
  color: "text-[#124011]",
  bg: "bg-[#C3FBB5]",
};

export const LIST_FEEDBACK_STATUS_PILL_VARIANTS = {
  [LIST_STATUS_MEETING_FEEDBACK.QUALIFIED]: POSITIVE_OUTCOME_CONFIG,
  [LIST_STATUS_MEETING_FEEDBACK.UNQUALIFIED]: {
    icon: X,
    color: "text-[#B90000]",
    bg: "bg-[#FEDFDF]",
  },
  [LIST_STATUS_MEETING_FEEDBACK.SHOW_UP]: POSITIVE_OUTCOME_CONFIG,
  [LIST_STATUS_MEETING_FEEDBACK.PENDING]: {
    icon: Clock8,
    color: "text-black",
    bg: "bg-[#ECECEC]",
  },
  [LIST_STATUS_MEETING_FEEDBACK.NO_SHOW]: {
    icon: MessageCircleOff,
    color: "text-black",
    bg: "bg-[#ECECEC]",
  },
  [LIST_STATUS_MEETING_FEEDBACK.CANCELED]: {
    icon: X,
    color: "text-black",
    bg: "bg-[#ECECEC]",
  },
  [LIST_STATUS_MEETING_FEEDBACK.RESCHEDULED]: {
    icon: CalendarSync,
    color: "text-white",
    bg: "bg-[#F86767]",
  },
} as const;

export const LIST_STATUS_PILL_LABEL_OVERRIDES = {
  [SYSTEM_LIST_TYPES.COLD_OUTREACH_RESPONSE]: "Replied",
} as const;

export const PIPELINE_LIST_TYPES = {
  DEFAULT: "default",
  CUSTOM: "custom",
  SYSTEM_LIST: "system_list",
  NURTURE_LIST: "nurture_list",
  COLD_LIST: "cold_list",
  DIALER: "dialer",
} as const;

/**
 * System lists, which are considered nurture lists (former `Callbacks` and
 * `Missed Calls`).
 */
export const SYSTEM_NURTURE_LIST_TYPES = [
  LIST_TYPES.CALLBACKS,
  LIST_TYPES.MISSED_CALLS,
];

export const NURTURE_LIST_TYPES = [
  ...SYSTEM_NURTURE_LIST_TYPES,
  LIST_TYPES.TODO_LIST,
  LIST_TYPES.CUSTOM,
  LIST_TYPES.EXCLUSIVE,
  LIST_TYPES.HUBSPOT_LEAD_PULLS,
  LIST_TYPES.SALESFORCE_LEAD_PULLS,
];

export const LIST_SUBSECTIONS_MAP = {
  NURTURE: "nurture_leads",
  COLD_CALL: "cold_leads",
} as const;

export const LIST_SUBSECTIONS_LABEL_MAP = {
  [LIST_SUBSECTIONS_MAP.NURTURE]: "Nurture",
  [LIST_SUBSECTIONS_MAP.COLD_CALL]: "Cold",
} as const;

export const LIST_SUBSECTION_DESCRIPTIONS_MAP = {
  [LIST_SUBSECTIONS_MAP.NURTURE]: "Being worked, touch points made",
  [LIST_SUBSECTIONS_MAP.COLD_CALL]: "No conversations yet",
} as const;

export const CONTACT_TYPES = {
  DECISION_MAKER: "decision_maker",
  INFLUENCER: "influencer",
  GATEKEEPER: "gatekeeper",
  NOT_RELEVANT: "not_relevant",
  BAD_DATA: "bad_data",
  DO_NOT_CALL: "do_not_call",
  FILTERED_OUT_FROM_CRM: "filtered_out_from_crm",
  CUSTOMER_DO_NOT_CALL: "customer_do_not_call",
} as const;

export const CONTACT_TYPES_LABEL_MAP = {
  [CONTACT_TYPES.DECISION_MAKER]: "Decision maker",
  [CONTACT_TYPES.INFLUENCER]: "Influencer",
  [CONTACT_TYPES.GATEKEEPER]: "Gatekeeper",
  [CONTACT_TYPES.NOT_RELEVANT]: "Not relevant",
  [CONTACT_TYPES.BAD_DATA]: "Bad data",
  [CONTACT_TYPES.DO_NOT_CALL]: "Do not call",
  [CONTACT_TYPES.FILTERED_OUT_FROM_CRM]: "Filtered out from CRM",
  [CONTACT_TYPES.CUSTOMER_DO_NOT_CALL]: "Customer suppressed",
} as const;

/**
 * These options are blocked from being selected in the UI edit-mode input.
 * But contact_type can still equal to them by backend logic.
 * If contact_type equals to one of these options, it will be blocked from editing.
 */
export const CONTACT_TYPES_BLOCKED_OPTIONS = [
  CONTACT_TYPES.FILTERED_OUT_FROM_CRM,
  CONTACT_TYPES.CUSTOMER_DO_NOT_CALL,
] as const;

export const ACCOUNT_STATUSES = {
  COLD_LEAD: "cold_lead",
  NURTURE: "nurture",
  MEETING_SCHEDULED: "meeting_scheduled",
  FEEDBACK_RECEIVED: "feedback",
  DISQUALIFIED: "disqualified",
} as const;

export const ACCOUNT_STATUS_LABEL_MAP = {
  [ACCOUNT_STATUSES.COLD_LEAD]: "Cold",
  [ACCOUNT_STATUSES.NURTURE]: "Nurture",

  // Feedback is not actually used and added here to avoid TS errors
  [ACCOUNT_STATUSES.FEEDBACK_RECEIVED]: "Feedback",

  [ACCOUNT_STATUSES.MEETING_SCHEDULED]: "Meeting Scheduled",
  [ACCOUNT_STATUSES.DISQUALIFIED]: "Disqualified",
} as const;
